import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Trash2, Copy, Edit3, RefreshCw, Megaphone } from 'lucide-react';
import HourglassLoader from './ui/HourglassLoader';
import apiClient from '../lib/apiClient';
import storage from '../lib/storage';
import manualEditorService from '../domain/manual-editor/services/manualEditorService';
import { MediaPicker } from '../domain/media';
import { googleAdsService } from '../services';
import Tooltip from '../ui/Tooltip/Tooltip';
import './Contents.css';

function Contents() {
  const navigate = useNavigate();
  const [contents, setContents] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editText, setEditText] = useState('');
  const [selectedLibraryImage, setSelectedLibraryImage] = useState(null);
  const [promoteLoadingId, setPromoteLoadingId] = useState(null);
  const [promoteMessage, setPromoteMessage] = useState('');

  const loadContents = async () => {
    setLoading(true);
    try {
      const token = storage.get('authToken');
      const sessionId = storage.get('guest_session_id');

      if (token) {
        const data = await apiClient.request('/user/posts');
        if (data?.success) {
          setContents(data.posts || []);
        }
      } else {
        const guestData = await apiClient.request(`/contents?session_id=${sessionId || ''}`, { auth: false });
        if (guestData?.success) setContents(guestData.contents || []);
      }

      const designData = await manualEditorService.listDesigns(token ? null : sessionId);
      if (designData?.success) {
        setDesigns(designData.designs || []);
      }
    } catch (error) {
      console.error('Erro ao carregar conteúdos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContents();
  }, []);

  const filtered = useMemo(() => {
    const allItems = [
      ...contents.map((item) => ({ ...item, kind: 'post' })),
      ...designs.map((item) => ({
        ...item,
        kind: 'design',
        content_type: item.type === 'carrossel' ? 'carrossel' : 'design',
      })),
    ];

    return allItems.filter((item) => {
      const typeOk = filter === 'all' || item.content_type === filter || (filter === 'design' && item.kind === 'design');
      const text = `${item.content || ''} ${item.prompt || ''} ${item.platform || ''}`.toLowerCase();
      const searchOk = text.includes(search.toLowerCase());
      return typeOk && searchOk;
    });
  }, [contents, designs, filter, search]);

  const deleteItem = async (item) => {
    if (!window.confirm('Excluir este conteúdo?')) return;
    try {
      if (item.kind === 'design') {
        const data = await manualEditorService.destroy(item.id);
        if (data?.success) setDesigns((prev) => prev.filter((design) => design.id !== item.id));
        return;
      }

      const data = await apiClient.request(`/user/posts/${item.id}`, { method: 'DELETE' });
      if (data?.success) setContents((prev) => prev.filter((post) => post.id !== item.id));
    } catch (error) {
      console.error('Erro ao excluir conteúdo:', error);
    }
  };

  const duplicateItem = async (item) => {
    try {
      if (item.kind === 'design') {
        const data = await manualEditorService.duplicate(item.id);
        if (data?.success && data.design) {
          setDesigns((prev) => [data.design, ...prev]);
        }
        return;
      }

      const data = await apiClient.request(`/user/posts/${item.id}/duplicate`, { method: 'POST' });
      if (data?.success && data.post) {
        setContents((prev) => [data.post, ...prev]);
      }
    } catch (error) {
      console.error('Erro ao duplicar conteúdo:', error);
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setEditText(item.content || '');
  };

  const saveEdit = async () => {
    if (!editingItem) return;
    try {
      const data = await apiClient.request(`/user/posts/${editingItem.id}`, {
        method: 'PUT',
        body: JSON.stringify({ content: editText })
      });
      if (data?.success) {
        setContents((prev) => prev.map((item) => (item.id === editingItem.id ? { ...item, content: editText } : item)));
        setEditingItem(null);
        setEditText('');
      }
    } catch (error) {
      console.error('Erro ao editar conteúdo:', error);
    }
  };

  const copyContent = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const getItemMediaUrl = (item) => {
    if (!item) return '';

    if (item.kind === 'design') {
      return (
        item.media_url ||
        item.background_image_url ||
        item.images?.[0]?.url_regular ||
        item.images?.[0]?.url_full ||
        item.images?.[0]?.url ||
        item.images?.[0]?.thumb ||
        ''
      );
    }

    return item.image_url || item.media_url || item.thumbnail_url || '';
  };

  const promoteWithGoogleAds = async (item) => {
    setPromoteMessage('');
    setPromoteLoadingId(item.id);

    try {
      const status = await googleAdsService.getStatus();
      if (!status?.success || !status?.connected) {
        const connection = await googleAdsService.getConnectUrl();
        if (connection?.success && connection?.auth_url) {
          window.location.assign(connection.auth_url);
          return;
        }
        setPromoteMessage('Conecte sua conta Google Ads para promover este conteúdo.');
        return;
      }

      const campaignType = window.prompt('Tipo de campanha (demand_gen ou performance_max):', 'demand_gen');
      if (!campaignType) return;

      const budgetRaw = window.prompt('Budget diário em R$:', '50');
      if (!budgetRaw) return;

      const audience = window.prompt('Audiência (opcional):', 'empreendedores digitais') || '';

      const mediaUrl = getItemMediaUrl(item);
      const payload = {
        type: campaignType === 'performance_max' ? 'performance_max' : 'demand_gen',
        budget: Number(budgetRaw),
        audience,
        customer_id: status?.account?.google_ads_customer_id || undefined,
        post_id: item.id,
        media_url: mediaUrl || undefined,
        media_type: item.content_type === 'reels' ? 'video' : 'photo',
        name: `Promoção ${item.content_type || 'post'} ${new Date().toLocaleDateString('pt-BR')}`,
      };

      const response = await googleAdsService.createCampaign(payload);
      if (!response?.success) {
        setPromoteMessage(response?.error || response?.message || 'Falha ao criar campanha.');
        return;
      }

      setPromoteMessage('Campanha Google Ads criada com sucesso.');
    } catch (error) {
      console.error(error);
      setPromoteMessage('Erro ao promover conteúdo com Google Ads.');
    } finally {
      setPromoteLoadingId(null);
    }
  };

  const renderItemThumbnail = (item) => {
    const fallback = getItemMediaUrl(item);
    const designImage = item.images?.[0]?.thumb || item.images?.[0]?.url || fallback;
    const src = item.kind === 'design' ? designImage : fallback;

    if (!src) return null;

    return (
      <div className="content-thumbnail">
        <img src={src} alt={item.prompt || 'Conteúdo'} />
      </div>
    );
  };

  return (
    <div className="contents-page">
      <div className="contents-header">
        <button className="btn btn-outline" onClick={loadContents}>
          <div className="blob1"></div>
          <div className="blob2"></div>
          <div className="inner">
            <RefreshCw size={16} /> Recarregar
          </div>
        </button>
      </div>

      <div className="contents-layout">
        <main className="contents-primary">
          {loading && <HourglassLoader variant="inline" message="Carregando conteúdos..." />}

          {!loading && (
            <div className="contents-grid">
              {filtered.map((item) => (
                <article key={item.id} className="content-card card">
                  <div className="content-info">
                    <span className="badge badge-primary">{item.content_type || 'post'}</span>
                    <h3>{item.prompt || 'Conteúdo salvo'}</h3>
                    <p>
                      {item.kind === 'design'
                        ? `${item.platform || 'instagram'} • ${item.size || '1:1'} • ${item.format || 'standard'}`
                        : item.content}
                    </p>
                  </div>
                  {renderItemThumbnail(item)}
                  <div className="content-actions">
                    <button
                      className="icon-btn"
                      onClick={() => copyContent(item.kind === 'design' ? item.prompt : item.content)}
                      aria-label="Copiar conteúdo"
                    >
                      <Copy size={16} />
                    </button>
                    <Tooltip content="Edite este conteúdo para ajustar texto, layout ou elementos visuais antes de publicar." variant="info">
                      <button
                        className="icon-btn"
                        onClick={() => (item.kind === 'design' ? navigate(`/manual-create?designId=${item.id}`) : startEdit(item))}
                        aria-label={item.kind === 'design' ? 'Abrir editor' : 'Editar'}
                      >
                        <Edit3 size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip content="Crie uma cópia deste conteúdo para testar variações sem perder a versão original." variant="tip">
                      <button className="icon-btn" onClick={() => duplicateItem(item)} aria-label="Duplicar conteúdo">
                        <FileText size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip content="Exclui permanentemente este conteúdo da biblioteca. Use com atenção." variant="warning">
                      <button className="icon-btn delete" onClick={() => deleteItem(item)} aria-label="Excluir conteúdo">
                        <Trash2 size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip content="Promova este conteúdo direto no Google Ads com um clique." variant="tip">
                      <button
                        className="icon-btn"
                        onClick={() => promoteWithGoogleAds(item)}
                        aria-label="Promover com Google Ads"
                        disabled={promoteLoadingId === item.id}
                      >
                        <Megaphone size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </article>
              ))}

              {!filtered.length && !loading && (
                <div className="empty-state card">
                  <h3>Nenhum conteúdo encontrado</h3>
                  <p>Tente mudar filtros ou gerar novos conteúdos.</p>
                </div>
              )}
            </div>
          )}
        </main>

        <aside className="contents-secondary">
          <section className="contents-filters">
            <h3 className="contents-filters-title">Filtro</h3>
            <div className="contents-toolbar">
              <input
                className="search-input"
                placeholder="Buscar conteúdo"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <select value={filter} onChange={(event) => setFilter(event.target.value)}>
                <option value="all">Todos</option>
                <option value="post">Post</option>
                <option value="stories">Stories</option>
                <option value="reels">Reels</option>
                <option value="carrossel">Carrossel</option>
                <option value="design">Design</option>
              </select>
            </div>
          </section>

          {editingItem && (
            <section className="contents-filters">
              <h3 className="contents-filters-title">Editar Conteúdo</h3>
              <textarea value={editText} onChange={(event) => setEditText(event.target.value)} rows={6} />
              <div className="contents-toolbar">
                <button className="btn btn-outline" onClick={saveEdit}>Salvar</button>
                <button className="btn btn-secondary" onClick={() => setEditingItem(null)}>Cancelar</button>
              </div>
            </section>
          )}

          <section className="contents-filters">
            <h3 className="contents-filters-title">Biblioteca de Mídia</h3>
            <MediaPicker type="photo" value={selectedLibraryImage} onChange={setSelectedLibraryImage} />
            {selectedLibraryImage && (
              <div className="contents-image-credit">
                <small>
                  Foto por {selectedLibraryImage.author || 'Pexels'} ·
                  {' '}
                  <a href={selectedLibraryImage.author_url || 'https://www.pexels.com'} target="_blank" rel="noreferrer">Pexels</a>
                </small>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => {
                    const imageUrl = encodeURIComponent(selectedLibraryImage.regular || selectedLibraryImage.small || selectedLibraryImage.thumb || '');
                    navigate(`/manual-create?imageUrl=${imageUrl}`);
                  }}
                >
                  Abrir no Editor Manual
                </button>
              </div>
            )}

            {promoteMessage && (
              <div className="contents-image-credit">
                <small>{promoteMessage}</small>
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

export default Contents;
